import React, { useCallback, useEffect, useRef, useState } from 'react'
import PropTypes from 'prop-types'
import debounce from 'lodash/debounce'
import ButtonVariantEnum from '../../enums/ButtonVariant'
import SearchByKeywordsEnum from '../../enums/jobSearch/SearchByKeywordsEnum'
import SearchEnum from '../../enums/jobSearch/SearchEnum'
import RoutingEnum from '../../routes/appRoutes'
import { useClickOutside } from '../../hooks/useClickOutSide'

import {
  SearchIcon,
  StyledButtonText,
  StyledChildrenContainer,
  StyledContainer,
  StyledInput,
  StyledInputContainer,
  StyledInputIconButton,
  StyledParagraph,
  StyledSearchBar,
  StyledSearchButton,
  StyledSearchForm,
} from './SearchBarSmall.styled'
import Autocompletion from '../Autocompletion/Autocompletion'
import { NAVBAR_HEIGHT } from '../../theme/constants'
import useMediaQuery from '../../hooks/useMediaQuery'
import { deviceMax } from '../../theme/breakpoints'
import pxToRem from '../../utils/pxToRem'
import ClearIcon from '../../images/icons/close-fill.svg'


const GET = 'GET'
const INPUT_NAME = 'keywords'
const INPUT_TYPE = 'text'
const PLACEHOLDER = 'Search by keywords...'
const SEARCH_LABEL = 'Search Jobs'
const LABEL =  'Search Constellium Jobs'

const SearchBarSmall = (props) => {
  const {
    children,
    handleAutocompletion,
    handleNewSearch,
    setKeywords,
    keywords,
    suggestions,
    locale,
    isJobSearchPage = false
  } = props

  const [inputFocused, setInputFocused] = useState(false)
  const [scroll, setScroll] = useState(false)
  const isTablet = useMediaQuery(deviceMax.tablet)

  const handleOnSubmit = (event) => {
    event.preventDefault()
    setInputFocused(false)
    handleNewSearch(true)
  }

  const launchAutocomplete = useCallback(
    debounce(handleAutocompletion, 300),
    []
  )

  const formRef = useRef(null)
  useClickOutside(formRef, () => setInputFocused(false))

  const changeKeywords = (keywords) => {
    setKeywords(keywords)
    launchAutocomplete(keywords)
  }

  const handleOnChange = (event) => {
    changeKeywords(event.target.value)
  }

  const handleOnChangeAutocompletion = (suggest) => {
    changeKeywords(suggest)
  }

  let oldScrollY = 0

  const controlDirection = () => {
    const navbarHeight = isTablet ? NAVBAR_HEIGHT.mobile : NAVBAR_HEIGHT.desktop
    if (window.scrollY > oldScrollY && window.scrollY > navbarHeight) {
      setScroll(true)
    } else {
      setScroll(false)
    }
    oldScrollY = window.scrollY
  }

  useEffect(() => {
    window.addEventListener('scroll', controlDirection)
    return () => window.removeEventListener('scroll', controlDirection)
  }, [])

  return (
    <StyledContainer $scroll={scroll}>
      <StyledSearchBar className="requestScrollTo">
        <StyledParagraph>{LABEL}</StyledParagraph>
        <StyledSearchForm
          action={RoutingEnum.search}
          inputFocused={inputFocused && suggestions.length}
          method={GET}
          onSubmit={handleOnSubmit}
          ref={formRef}
          role="search"
        >
          <StyledInputContainer>
            <StyledInput
              aria-label={INPUT_NAME}
              autoComplete='off'
              name={INPUT_NAME}
              onChange={handleOnChange}
              onFocus={() => setInputFocused(true)}
              placeholder={locale ? SearchByKeywordsEnum[locale] : PLACEHOLDER}
              type={INPUT_TYPE}
              value={keywords}
            />
            {isJobSearchPage && keywords !== '' && <StyledInputIconButton
              width={pxToRem(24)}
              height={pxToRem(24)}
              onClick={() => changeKeywords('')}
            >
              <ClearIcon />
            </StyledInputIconButton>}
          </StyledInputContainer>
          <StyledSearchButton
            variant={ButtonVariantEnum.primary}
          >
            <StyledButtonText>
              {locale ? SearchEnum[locale] : SEARCH_LABEL}
            </StyledButtonText>
            <SearchIcon $isMobile={true} />
          </StyledSearchButton>
          {inputFocused && suggestions.length ? (
            <Autocompletion
              handleOnChange={handleOnChangeAutocompletion}
              suggestions={suggestions}
              smallSearchbar={true}
            />
          ) : null}
        </StyledSearchForm>
        <StyledChildrenContainer>{children}</StyledChildrenContainer>
      </StyledSearchBar>
    </StyledContainer>
  )
}

SearchBarSmall.propTypes = {
  children: PropTypes.any,
  handleAutocompletion: PropTypes.func,
  handleNewSearch: PropTypes.func.isRequired,
  search: PropTypes.string,
  suggestions: PropTypes.arrayOf(PropTypes.string),
  text: PropTypes.string,
  locale: PropTypes.string,
}

export default SearchBarSmall
